import { useQuery } from "@apollo/client";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  GridLegacy as Grid,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { ConfirmPersonChangesModal } from "components/Contacts/ConfirmPersonChangesModal";
import { EditPerson } from "components/Contacts/EditPerson";
import { Dialog } from "components/Dialog";
import { DateField } from "components/Forms/DateField";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { FormSelect } from "components/Forms/FormSelect";
import { IssueListButton } from "components/Forms/IssueListButton";
import { PersonPicker } from "components/PersonPicker";
import { gql } from "generated-graphql";
import {
  ContactReportRole,
  FacilityContactsFragment,
  GetPersonQuery,
  Issue,
  Permission,
  PersonPickerFragment,
  RcraInfoLandType,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useEpcraContactRoles, useRcraContactRoles } from "hooks/useEpcraContactRoles";
import { usePaginationModel } from "hooks/usePaginationModel";
import { useValidatingForm } from "hooks/useValidatingForm";
import { Fragment, useCallback, useMemo, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  RCRA_LAND_TYPE_LABELS,
  selectItemsFromLabels,
} from "routes/Customer/Facility/ProfileV2/rcraProfileLabels";
import { useFacility } from "routes/Customer/Facility/useFacility";
import { hasCriticalIssues } from "util/forms";
import { v4 as uuid } from "uuid";

import { useFacilityContactInputValidation } from "../../routes/Customer/Chemicals/Report/validationHooks/useFacilityContactInputValidation";
import { contactRoleToLabel } from "../../util/facilityContacts";

type Row = NonNullable<FacilityContactsFragment["contacts"]>[number];

const GET_PERSON_FACILITIES = gql(`
  query GetPersonFacilities($personId: ID!, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    personFacilities(id: $personId, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
        city
        state
      }
      count
    }
  }
`);

const GET_PERSON = gql(`
  query GetPerson($id: ID!) {
    person(id: $id) {
      id
      first
      last
      phones {
        number
        type
      }
      renderPhone {
        inputMask
      }
      email
      title
      company

      attentionTo
      streetAddress1
      streetAddress2
      city
      country
      state
      zip

      tankOwnerType

      dbNumber

      user {
        id
        email
      }
    }
  }
`);

export type EditContactFormData = {
  id?: string;
  reportingRoles: ContactReportRole[];
  person: GetPersonQuery["person"];
  isPrimaryContact?: boolean;
  rcraOwnerOperatorType?: RcraInfoLandType | null;
  rcraDateBecameCurrent?: string | null;
  rcraDateEndedCurrent?: string | null;
  rcraComments?: string | null;
};

export const EditFacilityContactDialog = ({
  onClose,
  onSave,
  data,
  contacts,
  addingContact,
  setEditRow,
  facilityId,
  initialIssues,
  excludeInternalRoles = false,
  saving = false,
}: {
  onClose: () => void;
  onSave: (
    roles: ContactReportRole[],
    /**
     * The roles this dialog is authoritative for (every role checkbox it
     * rendered). Existing roles outside this set are preserved on save.
     */
    managedReportingRoles: ContactReportRole[],
    person: GetPersonQuery["person"],
    isPrimaryContact?: boolean,
    rcraFields?: {
      rcraOwnerOperatorType?: RcraInfoLandType | null;
      rcraDateBecameCurrent?: string | null;
      rcraDateEndedCurrent?: string | null;
      rcraComments?: string | null;
    }
  ) => void;
  data: Row | null;
  contacts: Row[];
  addingContact: ContactReportRole | undefined;
  setEditRow: (row: Row | null) => void;
  facilityId: string;
  initialIssues: Issue[];
  /**
   * Whether to exclude internal-only roles (e.g., Regional Environmental Contact).
   * These roles are for internal tracking and should not appear on reports.
   * Set to true when editing contacts in the context of a TierII report.
   * @default false
   */
  excludeInternalRoles?: boolean;
  /**
   * When true, the primary action button shows a loading state and is
   * disabled. The dialog remains open. Use this to surface an external
   * save mutation's in-flight state (callers that own the mutation
   * themselves, rather than the dialog).
   * @default false
   */
  saving?: boolean;
}) => {
  const [selectedPerson, setSelectedPerson] = useState<PersonPickerFragment | null>(
    data?.person ?? null
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [paginationModel, setPaginationModel] = usePaginationModel(5);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "name", sort: "asc" }]);
  const { tenantId } = useParams();
  const facility = useFacility(facilityId);

  const theme = useTheme();
  // On small screens the side-by-side person/roles layout squeezes the person
  // form (and its state dropdown) until it is unreadable, so stack vertically.
  const stackVertically = useMediaQuery(theme.breakpoints.down("md"));

  const { hasPermissionForFacility } = useAuthorization();
  const readOnly =
    !hasPermissionForFacility(facilityId, [Permission.WriteFacilityContact]) ||
    data?.person.isEncamp === true;

  const { data: person, loading } = useQuery(GET_PERSON, {
    variables: { id: selectedPerson?.id ?? "" },
    skip: !selectedPerson || !selectedPerson.id,
    fetchPolicy: "cache-and-network",
  });

  const {
    data: personFacilitiesData,
    previousData,
    loading: facilityDataLoading,
  } = useQuery(GET_PERSON_FACILITIES, {
    variables: {
      personId: selectedPerson?.id ?? "",
      pageSize: paginationModel.pageSize,
      page: paginationModel.page,
      sort: [...sortModel],
    },
    skip: !(selectedPerson?.id ?? "").length,
    fetchPolicy: "cache-and-network",
  });
  const { items: facilities, count: facilityCount } = personFacilitiesData?.personFacilities ??
    previousData?.personFacilities ?? { count: 0, items: [] };

  const defaultValues: EditContactFormData = useMemo(() => {
    let reportingRoles = data?.reportingRoles ?? [];

    if (addingContact) {
      reportingRoles = data?.reportingRoles
        ? [...data.reportingRoles, addingContact]
        : [addingContact];
    }

    return {
      id: data?.id ?? "",
      reportingRoles,
      isPrimaryContact: data?.isPrimaryContact ?? undefined,
      rcraOwnerOperatorType: data?.rcraOwnerOperatorType ?? undefined,
      rcraDateBecameCurrent: data?.rcraDateBecameCurrent ?? undefined,
      rcraDateEndedCurrent: data?.rcraDateEndedCurrent ?? undefined,
      rcraComments: data?.rcraComments ?? undefined,
      person: selectedPerson
        ? (person?.person ?? {
            id: selectedPerson.id,
            first: selectedPerson.first,
            last: selectedPerson.last,
            email: selectedPerson.email,
          })
        : { id: uuid() },
    };
  }, [
    selectedPerson,
    person?.person,
    data?.reportingRoles,
    data?.id,
    data?.isPrimaryContact,
    data?.rcraOwnerOperatorType,
    data?.rcraDateBecameCurrent,
    data?.rcraDateEndedCurrent,
    data?.rcraComments,
    addingContact,
  ]);

  const changeSelectedPerson = (person: PersonPickerFragment | null) => {
    const contact = contacts?.find((c) => c.person.id === person?.id);
    const currentValues = getValues();

    if (contact) {
      setEditRow({
        ...contact,
        // this will add preselected roles to the contact with their existing roles
        reportingRoles:
          contact.reportingRoles.length > 0
            ? Array.from(new Set([...contact.reportingRoles, ...currentValues.reportingRoles]))
            : currentValues.reportingRoles,
      });
    } else if (currentValues.reportingRoles.length > 0) {
      setEditRow({
        reportingRoles: currentValues.reportingRoles,
        id: data?.id ?? "",
        person: person
          ? {
              id: person.id,
              first: person.first,
              last: person.last,
              email: person.email,
              tenantId: tenantId ?? "",
            }
          : { id: uuid(), tenantId: tenantId ?? "" },
      });
    }

    setSelectedPerson(person);
  };

  const { roles: epcraRoles } = useEpcraContactRoles({
    states: [facility.data?.facility?.state],
    excludeInternalRoles,
  });
  const { roles: rcraRoles } = useRcraContactRoles(facility.data?.facility?.state);

  // Single source of truth for the role checkboxes the dialog renders. Both the
  // rendered sections and the set of roles the dialog is authoritative for derive
  // from this, so they can never drift apart. Empty groups are dropped (e.g. RCRA
  // roles when the facility profile flag is off).
  const roleGroups = useMemo(
    () =>
      [
        { key: "epcra", heading: "EPCRA Reporting Roles *", options: epcraRoles },
        { key: "rcra", heading: "RCRA Reporting Roles", options: rcraRoles },
      ].filter((group) => group.options.length > 0),
    [epcraRoles, rcraRoles]
  );

  // The roles this dialog is authoritative for are exactly the checkboxes it
  // renders. Roles outside this set (internal-only, feature-flag-hidden, GENERAL)
  // are preserved by the backend on save.
  const managedReportingRoles = useMemo(
    () => roleGroups.flatMap((group) => group.options),
    [roleGroups]
  );

  const form = useValidatingForm<EditContactFormData>(
    defaultValues,
    initialIssues,
    useFacilityContactInputValidation(facilityId, managedReportingRoles)
  );
  const { handleSubmit, formState, control, issues: formIssues, getValues } = form;

  const getRcraFields = (data: EditContactFormData) => {
    const hasRcraRole = data.reportingRoles.some((r) => r.startsWith("RCRA_"));
    if (!hasRcraRole) {
      return {
        rcraOwnerOperatorType: null,
        rcraDateBecameCurrent: null,
        rcraDateEndedCurrent: null,
        rcraComments: null,
      };
    }
    return {
      rcraOwnerOperatorType: data.rcraOwnerOperatorType,
      rcraDateBecameCurrent: data.rcraDateBecameCurrent,
      rcraDateEndedCurrent: data.rcraDateEndedCurrent,
      rcraComments: data.rcraComments || null,
    };
  };

  const onSubmit = useCallback(
    async (data: EditContactFormData) => {
      const rcraFields = getRcraFields(data);
      if (
        data?.person?.id.length &&
        Object.keys(formState.dirtyFields.person ?? {}).length &&
        facilityCount > 1
      ) {
        setShowConfirmModal(true);
      } else {
        onSave(
          data.reportingRoles,
          managedReportingRoles,
          data.person,
          data.isPrimaryContact,
          rcraFields
        );
      }
    },
    [formState.dirtyFields.person, onSave, facilityCount, managedReportingRoles]
  );

  const editingMode = useMemo(() => {
    if (readOnly) {
      return "displayOnly";
    }
    if (selectedPerson && selectedPerson.id) {
      return "edit";
    } else {
      return "add";
    }
  }, [selectedPerson, readOnly]);

  const dialogTitle = useMemo(() => {
    if (editingMode === "displayOnly") {
      return "View Contact";
    }
    if (editingMode === "add") {
      return "Add Contact";
    }
    if (editingMode === "edit") {
      return "Edit Contact";
    }
    return "View Contact";
  }, [editingMode]);

  return (
    <Dialog
      open
      title={dialogTitle}
      onClose={onClose}
      maxWidth="md"
      primaryActionButtonProps={{
        disabled:
          editingMode === "displayOnly" ||
          hasCriticalIssues(formIssues) ||
          getValues().reportingRoles.length === 0 ||
          saving,
        loading: facilityDataLoading || saving,
        onClick: () => {
          handleSubmit(onSubmit)();
        },
      }}
      additionalPrimaryActionElements={[
        <IssueListButton issues={formIssues} key="issue-list-button" />,
      ]}
    >
      <form>
        {editingMode === "edit" ? (
          <Typography variant="body1" sx={{ mb: theme.spacing(3) }}>
            You cannot update this contact's email or name because it refers to a specific person in
            your organization. If you'd like to change the person associated with this reporting
            role, please create or select a new person from contact catalog. Reach out to your
            Encamp Customer Service Manager if you have additional questions.
          </Typography>
        ) : (
          <Grid item pt={theme.spacing(1)}>
            <PersonPicker
              disabled={data !== null}
              value={selectedPerson}
              label="Select Existing or Create New Contact"
              onSelectPerson={changeSelectedPerson}
            />
          </Grid>
        )}

        <Stack
          direction={stackVertically ? "column" : "row"}
          divider={<Divider orientation={stackVertically ? "horizontal" : "vertical"} flexItem />}
          spacing={theme.spacing(3)}
        >
          <Grid container spacing={theme.spacing(3)}>
            <Grid item xs={12}>
              <FormProvider {...form}>
                <EditPerson
                  mode={editingMode}
                  isStepper={true}
                  issues={formIssues}
                  isUser={!!person?.person?.user}
                  loading={loading}
                  showTankOwnerType={form
                    .getValues()
                    .reportingRoles.some((rr) => rr === ContactReportRole.EpcraCaTankOwner)}
                  showDunAndBradstreetNumber={form
                    .getValues()
                    .reportingRoles.some((rr) => rr === ContactReportRole.EpcraT2MParentCompany)}
                  nameHelperText={
                    facility.data?.facility?.state === "KS" &&
                    form.getValues().reportingRoles.includes(ContactReportRole.EpcraOwnerOperator)
                      ? "* The Owner/Operator contact should be for your business and not a singular person"
                      : undefined
                  }
                />
              </FormProvider>
            </Grid>
            {rcraRoles.length > 0 &&
              getValues().reportingRoles.some((r) => r.startsWith("RCRA_")) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: theme.spacing(1) }}>
                    RCRA Contact Details
                  </Typography>
                  <Stack spacing={theme.spacing(1)}>
                    <FormSelect
                      name="rcraOwnerOperatorType"
                      label="Owner/Operator Type"
                      control={control}
                      disabled={editingMode === "displayOnly"}
                      selectItems={selectItemsFromLabels<RcraInfoLandType>(RCRA_LAND_TYPE_LABELS)}
                    />
                    <DateField
                      name="rcraDateBecameCurrent"
                      label="Date Became Current"
                      control={control}
                      disabled={editingMode === "displayOnly"}
                    />
                    <DateField
                      name="rcraDateEndedCurrent"
                      label="Date Ended Current"
                      control={control}
                      disabled={editingMode === "displayOnly"}
                    />
                    <Controller
                      name="rcraComments"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ""}
                          label="Comments"
                          multiline
                          minRows={2}
                          maxRows={4}
                          disabled={editingMode === "displayOnly"}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Stack>
                </Grid>
              )}
          </Grid>

          <FormControl sx={{ width: { xs: "100%", md: theme.spacing(40) } }}>
            <Stack spacing={theme.spacing(1)}>
              {facility.data?.facility?.state === "CA" && (
                <>
                  <FormGroup>
                    <Controller
                      control={control}
                      name="isPrimaryContact"
                      render={({ field }) => (
                        <FormControlLabel
                          disabled={editingMode === "displayOnly"}
                          label={<Typography variant="body2">Primary Contact</Typography>}
                          control={
                            <Checkbox
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          }
                        />
                      )}
                    />
                  </FormGroup>
                  <Box sx={{ my: 1 }} />
                </>
              )}
              <Controller
                control={control}
                name="reportingRoles"
                render={({ field, fieldState: { error } }) => (
                  <FormGroup>
                    {roleGroups.map((group, index) => (
                      <Fragment key={group.key}>
                        {index > 0 && <Box sx={{ my: 2 }} />}
                        <Typography variant="subtitle2">{group.heading}</Typography>
                        {index === 0 && error && <ErrorDisplay error={error} sx={{ m: 0 }} />}
                        {group.options.map((option) => (
                          <FormControlLabel
                            key={option}
                            disabled={editingMode === "displayOnly"}
                            label={
                              <Typography variant="body2">{contactRoleToLabel(option)}</Typography>
                            }
                            control={
                              <Checkbox
                                checked={!!field.value?.includes(option)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    field.onChange([...(field.value ?? []), option]);
                                  } else {
                                    field.onChange(field.value?.filter((v) => v !== option));
                                  }
                                }}
                              />
                            }
                          />
                        ))}
                      </Fragment>
                    ))}
                  </FormGroup>
                )}
              />
            </Stack>
          </FormControl>
        </Stack>
      </form>
      {showConfirmModal && (
        <ConfirmPersonChangesModal
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          facilities={facilities}
          facilityCount={facilityCount}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={() =>
            handleSubmit(async (data) => {
              onSave(
                data.reportingRoles,
                managedReportingRoles,
                data.person,
                data.isPrimaryContact,
                getRcraFields(data)
              );
              setShowConfirmModal(false);
            })()
          }
          personFullName={`${selectedPerson?.first} ${selectedPerson?.last}`}
          saving={facilityDataLoading}
        />
      )}
    </Dialog>
  );
};
